import { useState } from 'react';
import { Info } from 'lucide-react';
import { Tooltip } from '@nextui-org/react';
import { VendorDetailRiskBadge } from '../atoms/VendorDetailRiskBadge';
import type { RiskLevel } from '../../../vendors-v2/types';
import { CriticalRiskDriver } from '../molecules/CriticalRiskDriver';
import { RiskPostureScore } from '../molecules/RiskPostureScore';
import { IntelligenceGrid } from './IntelligenceGrid';
import type {
	IntelligenceDrawerType,
	IntelligenceDrawerData,
} from '../../types/intelligence-drawer.types';
import type { ComplianceItem, IntelligenceEvent } from '../../types';
import { IntelligenceDrawer } from './IntelligenceDrawer';

interface RiskSummarySectionProps {
  overallRisk: RiskLevel | null;
  riskJustification: string | null;
  riskPostureScore: number | null;
  riskPostureMax: number | null;
  intelligence: {
    breachHistory: number;
    darkWebChatter: number;
    compliance: number;
    news: number;
  };
  className?: string;
  // Intelligence detail arrays
  breachHistoryEvents: IntelligenceEvent[];
  darkWebChatterFindings: IntelligenceEvent[];
  complianceItems: ComplianceItem[];
  newsArticles: IntelligenceEvent[];
}

export const RiskSummarySection = ({
  overallRisk,
  riskJustification,
  riskPostureScore,
  riskPostureMax,
  intelligence,
  className = '',
  breachHistoryEvents,
  darkWebChatterFindings,
  complianceItems,
  newsArticles,
}: RiskSummarySectionProps) => {
  // Drawer state management
  const [openDrawer, setOpenDrawer] = useState<IntelligenceDrawerType | null>(
    null,
  );

  const handleOpenDrawer = (type: IntelligenceDrawerType) => {
    setOpenDrawer(type);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(null);
  };

  // Prepare data for drawer
  const drawerData: IntelligenceDrawerData = {
    breachHistory: breachHistoryEvents,
    darkWebChatter: darkWebChatterFindings,
    compliance: complianceItems,
    news: newsArticles,
  };

  return (
    <>
      <style>{`
        .risk-summary-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .risk-summary-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .risk-summary-scroll::-webkit-scrollbar-thumb {
          background: transparent;
          border-radius: 3px;
        }
        .risk-summary-scroll:hover::-webkit-scrollbar-thumb {
          background: #94A3B8;
        }
      `}</style>
      <section
        className={`risk-summary-scroll flex flex-col items-center p-4 gap-4 bg-white shadow-[0px_1px_3px_rgba(0,0,0,0.1),0px_1px_2px_rgba(0,0,0,0.06)] rounded-lg h-[600px] overflow-y-auto ${className}`}
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: 'transparent transparent',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.scrollbarColor = '#94A3B8 transparent';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.scrollbarColor = 'transparent transparent';
        }}
      >
      {/* Section Header */}
      <h2 className="w-full font-['Inter'] font-medium text-sm leading-5 text-[#0F172B]">
        Risk Summary
      </h2>

      {/* Overall Risk Row */}
      <div className="flex flex-row items-center gap-3 w-full">
        <div className="flex flex-row items-start gap-1">
          <span className="font-['Inter'] font-normal text-sm leading-5 text-[#62748E]">
            Overall Risk
          </span>
          <Tooltip
            content="This represents the highest risk among all assessed products"
            placement="right"
          >
            <button
              type="button"
              className="flex items-center justify-center w-5 h-5 p-0.5"
              aria-label="Overall risk information"
            >
              <Info size={16} className="text-[#90A1B9]" />
            </button>
          </Tooltip>
        </div>
        {overallRisk && <VendorDetailRiskBadge risk={overallRisk} showRiskLabel={false} />}
      </div>

      {/* Critical Risk Driver */}
      {riskJustification && (
        <div className="w-full">
          <CriticalRiskDriver justification={riskJustification} />
        </div>
      )}

      {/* Divider */}
      <div className="w-full h-0 border-t border-[#E2E8F0]" />

      {/* Risk Posture Score */}
      <div className="w-full">
        <RiskPostureScore
          score={riskPostureScore}
          max={riskPostureMax || 100}
        />
      </div>

      {/* Divider */}
      <div className="w-full h-0 border-t border-[#E2E8F0]" />

      {/* Intelligence Section */}
      <h3 className="w-full font-['Inter'] font-medium text-sm leading-5 text-[#0F172B]">
        Intelligence
      </h3>

      <IntelligenceGrid
        breachHistoryCount={intelligence.breachHistory}
        darkWebChatterCount={intelligence.darkWebChatter}
        complianceCount={intelligence.compliance}
        newsCount={intelligence.news}
        onBreachHistoryClick={() => handleOpenDrawer('breachHistory')}
        onDarkWebChatterClick={() => handleOpenDrawer('darkWebChatter')}
        onComplianceClick={() => handleOpenDrawer('compliance')}
        onNewsClick={() => handleOpenDrawer('news')}
      />

      {/* Intelligence Details Drawer */}
      {openDrawer && (
        <IntelligenceDrawer
          isOpen={openDrawer !== null}
          onClose={handleCloseDrawer}
          drawerType={openDrawer}
          data={drawerData}
        />
      )}
    </section>
    </>
  );
};
